import type { GroupNode } from '../../grouping';
import { COLORS, fillForGroupLevel } from './styles';
import { writeDataRow } from './data-row';
import { writeSubtotalRow } from './subtotal-row';
import type { ExcelWriteContext } from './types';

/**
 * Emit a group header row — a single merged cell spanning the whole
 * worksheet width with the group label and the row count. Indent / color
 * scale with the group's level so the visual hierarchy is readable.
 *
 * `outlineLevel` is the collapse depth we want Excel to show on the left
 * margin. Group headers sit at their parent's level so they remain
 * visible when the group is collapsed — otherwise the `+`/`−` button
 * would vanish along with its children.
 */
function writeGroupHeader<T>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sheet: any,
  node: GroupNode<T>,
  totalCols: number,
  outlineLevel: number
): void {
  const rowRef = sheet.rowCount + 1;
  const row = sheet.addRow([]);
  sheet.mergeCells(rowRef, 1, rowRef, totalCols);

  const cell = row.getCell(1);
  cell.value = `${node.key}  (${node.rows.length})`;

  row.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: fillForGroupLevel(node.level) },
  };
  row.font = { bold: true, size: node.level === 0 ? 12 : 11 };
  row.alignment = { vertical: 'middle', horizontal: 'left' };
  row.eachCell((c: { border: unknown }) => {
    c.border = { bottom: { style: 'thin', color: { argb: COLORS.groupBorder } } };
  });
  if (outlineLevel > 0) {
    row.outlineLevel = outlineLevel;
  }
}

/**
 * Recursively write a group node and return the data-row range it covers.
 * The caller uses that range to build outer SUBTOTAL formulas.
 *
 * Depth-first traversal: group header → children (or leaf data) → subtotal.
 *
 * Outline-level convention (what Excel reads to draw the `+`/`−` buttons):
 *   - A group's header and its subtotal row share the SAME outline level
 *     as the group's parent — so when the group collapses, both stay
 *     visible and the expand button remains clickable.
 *   - A group's child rows (either leaf data or nested child groups)
 *     have an outline level one deeper than the group's own level.
 *   - Level 0 (outer market type) → header/subtotal at 0, children at 1
 *   - Level 1 (category) → header/subtotal at 1, children at 2
 *   - Etc.
 *
 * Returning `null` means the node had no data at all (rare, but possible
 * when upstream filtering produced an empty bucket).
 */
export function writeGroupNode<T>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sheet: any,
  node: GroupNode<T>,
  ctx: ExcelWriteContext<T>,
  hasSummary: boolean
): { firstDataRow: number; lastDataRow: number } | null {
  const groupOutlineLevel = node.level;
  const childOutlineLevel = node.level + 1;

  writeGroupHeader(sheet, node, ctx.columns.length, groupOutlineLevel);

  let firstData = -1;
  let lastData = -1;

  if (node.children && node.children.length > 0) {
    for (const child of node.children) {
      const range = writeGroupNode(sheet, child, ctx, hasSummary);
      if (range) {
        if (firstData === -1) firstData = range.firstDataRow;
        lastData = range.lastDataRow;
      }
    }
  } else {
    for (const row of node.rows) {
      const rowNumber = writeDataRow(sheet, row, ctx, childOutlineLevel);
      if (firstData === -1) firstData = rowNumber;
      lastData = rowNumber;
    }
  }

  if (hasSummary && firstData !== -1 && lastData !== -1) {
    writeSubtotalRow(
      sheet,
      firstData,
      lastData,
      node.rows,
      `Sous-total ${node.key}`,
      ctx,
      false,
      groupOutlineLevel
    );
  }

  if (firstData === -1 || lastData === -1) return null;
  return { firstDataRow: firstData, lastDataRow: lastData };
}
